import { useState, useCallback } from 'react';
import { NOTIFICATION_DURATION, MAX_NOTIFICATIONS } from '../lib/constants';

export type NotificationType = 'success' | 'error' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  addNotification: (type: NotificationType, message: string) => void;
  removeNotification: (id: string) => void;
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const addNotification = useCallback(
    (type: NotificationType, message: string) => {
      const id = crypto.randomUUID();
      const notification: Notification = { id, type, message };

      setNotifications((prev) => {
        const next = [...prev, notification];
        // Keep only the last MAX_NOTIFICATIONS
        if (next.length > MAX_NOTIFICATIONS) {
          return next.slice(-MAX_NOTIFICATIONS);
        }
        return next;
      });

      // Auto-remove after duration
      setTimeout(() => {
        removeNotification(id);
      }, NOTIFICATION_DURATION);
    },
    [removeNotification]
  );

  return { notifications, addNotification, removeNotification };
}
